/**
 * Le vaisseau : coque, ailes, nacelles, plumes de réacteur, traînée de fumée et
 * halo de ramassage.
 *
 * Le seul objet éclairé du jeu. Tout le reste est en `MeshBasicMaterial` ; le
 * vaisseau a un `MeshLambertMaterial` sous une lumière principale et une
 * ambiante faible, pour se lire comme un volume plein et pour que sa rotation
 * reste lisible dans une vrille.
 *
 * Deux choses reprises à dessein :
 *
 * - **`MeshLambertMaterial` n'a pas de `flatShading` en r128.** Une géométrie
 *   non indexée est déjà ombrée à plat ; poser la propriété ne fait que loguer
 *   un avertissement. Les coques sont bâties en triangles libres exactement
 *   pour cela.
 * - **La traînée de fumée est parentée au vaisseau, pas émise dans le monde.**
 *   En espace monde une bouffée lâchée au vaisseau croise nécessairement la
 *   caméra 19 m derrière et remplit l'écran. Parentée, la chaîne reste entre
 *   les deux et ne peut pas croiser.
 */
import {
  AdditiveBlending,
  BoxGeometry,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  Mesh,
  Material,
  MeshBasicMaterial,
  MeshLambertMaterial,
  SphereGeometry,
  Sprite,
  ShaderMaterial,
  SpriteMaterial,
} from 'three';
import type { ThrustTier } from '../sim/index.js';

/** Barreaux de poussée : croisière, boost, super boost, surge. */
const THRUST_LEVELS = [
  { len: 1.1, rad: 0.3, opacity: 0.35, colour: 0x5fd8ff },
  { len: 3.0, rad: 0.44, opacity: 0.75, colour: 0xbdf0ff },
  { len: 5.0, rad: 0.56, opacity: 0.95, colour: 0xff8ae0 },
  { len: 7.2, rad: 0.66, opacity: 1.0, colour: 0xfff0a8 },
] as const;

/** Couleur du cœur de flamme par palier. Le G-SURGE vire au blanc chaud. */
const CORE_BY_TIER = [0xffffff, 0xffffff, 0xffe6fb, 0xfff6d0] as const;

const SMOKE_COUNT = 18;
const TRAIL_LENGTH = 11;

/**
 * L'onde de choc de fin : une sphère qui enfle, pas un anneau à plat.
 *
 * L'anneau horizontal disait le pouls magnétique, mais vu de la caméra —
 * derrière et au-dessus — un disque qui s'ouvre se lit de biais et s'aplatit
 * dès qu'il dépasse les bords de la piste. Une sphère se lit pareil d'où
 * qu'on la regarde, ce qui est la seule propriété qui compte pour un effet
 * qu'on ne voit qu'une fois par partie et jamais deux fois du même angle.
 *
 * Deux coques concentriques, toutes deux additives : `SHOCKWAVE_*` est
 * l'enveloppe, large et faible, qui s'étend loin et s'éteint en s'étalant ;
 * `FLASH_*`, plus courte et plus brillante, est le cœur qui marque l'instant
 * de l'impact.
 *
 * L'enveloppe porte un fresnel, comme la bulle de `shield.ts` et pour la même
 * raison : une sphère additive à opacité constante accumule autant au centre
 * qu'au bord, et mesurée à l'écran elle se lit comme une bulle pleine qui
 * délave la scène, pas comme une onde. L'alpha suit donc l'incidence — nul là
 * où la surface fait face à la caméra, plein au silhouettage — ce qui ne
 * laisse briller que l'anneau, de n'importe quel angle. Aucun paramètre de
 * `MeshBasicMaterial` ne dépend de la vue, d'où le shader.
 */
const SHOCKWAVE_LIFE = 0.85;
const SHOCKWAVE_MAX_RADIUS = 20;
const SHOCKWAVE_COLOUR = 0x25e2ff;
const FLASH_LIFE = 0.14;
const FLASH_RADIUS = 5;

/** Combien l'anneau est serré : plus haut, plus fin. Réglé à l'œil. */
const SHOCKWAVE_RIM = 2.6;

/**
 * Éclipses par seconde de la coque pendant la grâce, et part du cycle passée
 * éteinte. Voir `setGrace`.
 *
 * Un tiers, pas la moitié : la position latérale du vaisseau est la seule chose
 * que le joueur pilote, et la grâce tombe précisément au moment où il essaie de
 * se replacer. Une coque absente un temps sur deux lui retirerait sa référence
 * juste là. Un tiers se lit comme un clignotement et laisse la silhouette
 * suivable.
 */
const GRACE_BLINK_HZ = 8;
const GRACE_BLINK_OFF = 1 / 3;

/**
 * Le battement des plumes : deux sinusoïdes par tuyère, en Hz, et l'amplitude
 * du battement en fraction de la longueur de base.
 *
 * **Pourquoi pas `Math.random`, qui était là.** Un tirage par frame n'a pas de
 * fréquence propre : il change aussi souvent que la machine affiche, donc le
 * même code bat quatre fois plus vite à 240 fps qu'à 60. Une horloge en
 * secondes bat pareil partout, ce qui est la seule façon d'accorder l'effet une
 * fois. C'est le raisonnement de `WAKE_*` juste en dessous, pour la traînée,
 * et il vaut ici mot pour mot.
 *
 * **Deux sinusoïdes et non une** : une seule se lit comme une pulsation
 * régulière, donc comme une animation. Deux fréquences sans rapport simple
 * battent l'une contre l'autre et ne se répètent qu'au bout de plusieurs
 * secondes, ce qui se lit comme de l'instabilité.
 *
 * **Des fréquences différentes d'une tuyère à l'autre, et non un déphasage** :
 * toutes les sinusoïdes partent donc de zéro, le battement vaut exactement 1
 * à l'horloge zéro, et `snapThrust` retrouve un état reproductible sans avoir
 * à connaître les phases. Les deux tuyères divergent dès la frame suivante.
 */
const PLUME_RATES: readonly (readonly [number, number])[] = [
  [7.3, 11.9],
  [8.7, 13.1],
];
const PLUME_WOBBLE = 0.16;

/**
 * Secondes après lesquelles l'horloge du battement revient à zéro.
 *
 * **Les quatre fréquences sont des multiples de 0,1 Hz, donc à dix secondes
 * chacune a bouclé un nombre entier de tours** : le repli est exact, aucune des
 * sinusoïdes ne saute. Toute fréquence ajoutée ici doit garder cette propriété.
 *
 * Sans le repli, l'horloge grandissait indéfiniment. Mesuré : à un million de
 * frames l'argument passé à `Math.sin` dépasse le million de radians, la
 * réduction d'argument devient le coût dominant, et la mise à jour des plumes
 * monte de 34 ns à 570 ns par frame. Replié, l'argument reste sous 830 rad.
 */
const PLUME_PERIOD = 10;

const TAU = Math.PI * 2;

/* Pas de `precision` déclarée : three.js pose `highp`, et le ciel a déjà payé
   ce qu'un `mediump` coûte ici. */
const SHOCKWAVE_VERT = `
varying vec3 vNormalW;
varying vec3 vViewW;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vNormalW = normalize(mat3(modelMatrix) * normal);
  vViewW = cameraPosition - world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const SHOCKWAVE_FRAG = `
uniform vec3 uColour;
uniform float uOpacity;
varying vec3 vNormalW;
varying vec3 vViewW;
void main() {
  float facing = abs(dot(normalize(vNormalW), normalize(vViewW)));
  float rim = pow(1.0 - facing, ${SHOCKWAVE_RIM.toFixed(1)});
  gl_FragColor = vec4(uColour, uOpacity * rim);
}
`;

/**
 * Ce qu'un drift fait à la traînée de fumée : elle se courbe vers le côté d'où
 * le vaisseau vient, puisque les bouffées ont été laissées là où il n'est plus,
 * et elle se disloque — chaque bouffée oscille pour son compte, d'autant plus
 * qu'elle est loin en arrière.
 *
 * Le FX_DRIFT_WAKE de la palette. Déterministe, une sinusoïde par bouffée sur
 * une horloge qui ne tourne que tant que le sillage est levé, plutôt qu'un
 * hasard par frame : la traînée est dans chaque capture de scène, et une
 * oscillation exactement nulle hors drift est ce qui tient ces captures en
 * place. Amorti, donc à remettre à zéro.
 */
const WAKE_BEND = 2.4;
const WAKE_JITTER = 0.55;
const WAKE_RATE = 9;
const WAKE_EASE = 6;

type Point3 = readonly [number, number, number];

/** Triangles libres : la géométrie est ombrée à plat sans le demander. */
function poly(
  faces: readonly (readonly [string, string, string])[],
  points: Record<string, Point3>,
): BufferGeometry {
  const v: number[] = [];
  for (const [a, b, c] of faces) v.push(...points[a]!, ...points[b]!, ...points[c]!);
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(v, 3));
  g.computeVertexNormals();
  return g;
}

export class Ship {
  /** Suit la piste. Fumée et halo y sont accrochés. */
  readonly group = new Group();
  /** Porte la gîte et le lacet visuels, que la coque montre mais pas la trajectoire. */
  readonly body = new Group();

  private readonly flames: Mesh[] = [];
  private readonly flameOuter: MeshBasicMaterial;
  private readonly flameCore: MeshBasicMaterial;
  private readonly halo: Mesh;
  private readonly haloMaterial: MeshBasicMaterial;
  private readonly smoke: Sprite[] = [];
  private smokeMap!: CanvasTexture;
  private smokePhase = 0;
  /** La glisse telle que la traînée la sent, −1 à 1, amortie. */
  private wake = 0;
  private wakeClock = 0;

  /**
   * L'horloge du battement des plumes, en secondes, et la géométrie de base
   * vers laquelle le barreau de poussée les amène.
   *
   * La base est ce qui s'amortit ; le battement se multiplie dessus, entier.
   * Voir `updateThrust` pour ce que l'inverse coûtait.
   */
  private plumeClock = 0;
  private plumeLen = 0;
  private plumeRad = 0;

  /** La coque d'onde qui enfle et le cœur bref qu'elle entoure. Voir `explode`. */
  private readonly shockwave: Mesh;
  private readonly shockwaveMaterial: ShaderMaterial;
  private readonly flash: Mesh;
  private readonly flashMaterial: MeshBasicMaterial;
  private exploding = false;
  /** Vrai dès l'explosion et jusqu'à `resetExplosion` : la coque reste cachée. */
  private destroyed = false;
  /**
   * L'épave, montrée quand l'onde s'est dissipée.
   *
   * La coque disparaissait pour de bon et laissait du vide à l'endroit exact
   * où le joueur regardait — pendant la tenue d'après-explosion, puis derrière
   * la carte de score, et pour l'autre pilote d'un duel. Une carcasse éteinte
   * et couchée dit ce qui s'est passé ; du vide ne dit rien.
   */
  private readonly wreck: Group;
  private explodeAge = 0;

  /** `prefers-reduced-motion` : lu une fois, comme dans `overlay.ts`. */
  private readonly damped =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Couleurs de travail, pour que les interpolations par frame n'allouent rien. */
  private readonly tmpA = new Color();
  private readonly tmpB = new Color();

  constructor() {
    this.group.add(this.body);
    this.buildHull();
    // Une seconde coque, éteinte : même géométrie, matériau mat. Elle est
    // bâtie une fois et cachée, comme tout le reste ici — rien ne s'alloue
    // au moment où la partie se perd.
    // Une braise plutôt qu'une silhouette noire : sur une route sombre, un
    // matériau seulement mat se confondait avec les marquages — vu sur une
    // capture. L'émissif est faible, elle n'éclaire rien autour d'elle.
    this.wreck = hullBody(new MeshLambertMaterial({ color: 0x2f343d, emissive: 0x3a1206 }));
    this.wreck.visible = false;
    this.group.add(this.wreck);
    ({ outer: this.flameOuter, core: this.flameCore } = this.buildPlumes());

    this.haloMaterial = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    this.halo = new Mesh(new SphereGeometry(2.6, 14, 10), this.haloMaterial);
    this.halo.position.y = 0.8;
    this.halo.visible = false;
    this.group.add(this.halo);

    // Les deux faces : le shader ne laisse passer que le silhouettage, donc
    // l'avant et l'arrière de la coque se superposent exactement sur l'anneau
    // et le doublent, ce qui est le but.
    this.shockwaveMaterial = new ShaderMaterial({
      uniforms: {
        uColour: { value: new Color(SHOCKWAVE_COLOUR) },
        uOpacity: { value: 0 },
      },
      vertexShader: SHOCKWAVE_VERT,
      fragmentShader: SHOCKWAVE_FRAG,
      transparent: true,
      side: DoubleSide,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    this.shockwave = new Mesh(new SphereGeometry(1, 28, 18), this.shockwaveMaterial);
    this.shockwave.position.y = 0.9;
    this.shockwave.visible = false;
    this.group.add(this.shockwave);

    this.flashMaterial = new MeshBasicMaterial({
      color: 0xe8fbff,
      transparent: true,
      opacity: 0,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    this.flash = new Mesh(new SphereGeometry(1, 20, 14), this.flashMaterial);
    this.flash.position.y = 0.9;
    this.flash.visible = false;
    this.group.add(this.flash);

    this.buildSmoke();
  }

  /** Place le vaisseau dans le repère de la piste. `bank` vient de la simulation. */
  setPose(lat: number, hop: number, bank: number): void {
    const cb = Math.cos(bank),
      sb = Math.sin(bank);
    const hover = 1.35 + hop;
    this.group.position.set(cb * lat - sb * hover, sb * lat + cb * hover, 0);
    this.group.rotation.z = bank;
  }

  /**
   * L'invulnérabilité brève, telle qu'elle se voit : la coque s'éclipse et
   * revient tant que la grâce court.
   *
   * La phase se lit sur le compteur de la simulation plutôt que sur une horloge
   * d'affichage, et c'est ce qui la rend sans entretien : il descend au pas
   * fixe, donc le clignotement bat pareil à toutes les cadences, il n'y a rien
   * à amortir ni à remettre à zéro avant une capture, et il finit forcément
   * coque visible puisqu'à zéro la branche ne s'ouvre pas.
   *
   * La bulle d'invincibilité, elle, n'est pas touchée : son propre clignotement
   * annonce une fin prochaine, celui-ci dit qu'on est encore intouchable, et
   * les deux ne doivent pas se prononcer sur le même objet. Voir `shield.ts`.
   *
   * `prefers-reduced-motion` l'éteint, comme il éteint le battement du voile de
   * dégâts dans `damage.ts` : une coque qui s'éclipse huit fois par seconde est
   * exactement ce que ce réglage existe pour éviter. Le joueur garde la grâce,
   * il ne la voit pas — la mécanique ne dépend pas de son affichage.
   *
   * @param graceT secondes d'invulnérabilité restantes, `state.graceT`.
   */
  setGrace(graceT: number): void {
    if (this.destroyed) return;
    if (graceT <= 0 || this.damped) {
      this.body.visible = true;
      return;
    }
    this.body.visible = (graceT * GRACE_BLINK_HZ) % 1 > GRACE_BLINK_OFF;
  }

  /** Attitude visuelle : roulis dans le virage, lacet avec la dérive. */
  setAttitude(lean: number, yaw: number, pitch: number): void {
    this.body.rotation.z = lean;
    this.body.rotation.y = yaw;
    this.body.rotation.x = pitch;
  }

  /**
   * @param level 0 croisière, 1 boost, 2 super boost, 3 surge.
   * @param frameDt vrai delta de frame : c'est un lissage d'affichage, pas de la simulation.
   */
  updateThrust(frameDt: number, level: ThrustTier): void {
    const lv = THRUST_LEVELS[level];
    this.plumeClock += frameDt;
    if (this.plumeClock >= PLUME_PERIOD) this.plumeClock -= PLUME_PERIOD;

    // C'est le barreau qu'on amortit, plus le battement.
    //
    // L'inverse était écrit ici, et c'est ce qui rendait les réacteurs figés :
    // un tirage aléatoire par frame passait par ce même amortissement à
    // `frameDt * 12`, soit un cinquième du chemin à 60 fps. La cible se
    // retirant au hasard autour de sa moyenne à chaque frame, l'amortissement
    // se comportait en filtre passe-bas et il ne restait presque rien des
    // ±14 % annoncés. Le scintillement était donc bien là, et invisible.
    //
    // La base — celle que le barreau déplace — garde l'amortissement, qui est
    // ce à quoi il sert : une montée en boost ne doit pas être un saut. Le
    // battement, lui, s'applique entier.
    const k = Math.min(1, frameDt * 12);
    this.plumeLen += (lv.len - this.plumeLen) * k;
    this.plumeRad += (lv.rad - this.plumeRad) * k;

    // Une tuyère à la fois : les deux cônes d'une même tuyère partagent son
    // battement — c'est un moteur, pas deux — mais les deux tuyères ont chacune
    // le sien. Elles battaient jusqu'ici sur la même valeur, ce qu'aucun moteur
    // réel ne fait et ce que l'œil relève même sans savoir pourquoi.
    const nozzles = this.flames.length >> 1;
    for (let n = 0; n < nozzles; n++) {
      const [a, b] = PLUME_RATES[n % PLUME_RATES.length]!;
      const t = this.plumeClock;
      const beat = 1 + PLUME_WOBBLE * 0.5 * (Math.sin(TAU * a * t) + Math.sin(TAU * b * t));
      for (let s = 0; s < 2; s++) {
        const m = this.flames[n * 2 + s]!;
        const isCore = s === 1;
        m.scale.z = this.plumeLen * (isCore ? 0.62 : 1) * beat;
        m.scale.x = this.plumeRad * (isCore ? 0.5 : 1);
        m.scale.y = m.scale.x;
      }
    }

    const ko = Math.min(1, frameDt * 8);
    this.flameOuter.opacity += (lv.opacity * 0.55 - this.flameOuter.opacity) * ko;
    this.flameCore.opacity += (lv.opacity - this.flameCore.opacity) * ko;
    const kc = Math.min(1, frameDt * 6);
    this.flameOuter.color.lerp(this.tmpA.setHex(lv.colour), kc);
    this.flameCore.color.lerp(this.tmpB.setHex(CORE_BY_TIER[level]), kc);
  }

  /**
   * Pose les plumes directement à leur état stable, sans scintillement.
   *
   * Seule la capture de frame déterministe s'en sert. Les plumes convergent
   * vers leur cible sur beaucoup de frames, donc une frame prise après un nombre
   * arbitraire d'entre elles n'est pas reproductible — ce qui s'est vu comme
   * 7 000 pixels de différence entre deux captures du même état figé.
   *
   * La base amortie et l'horloge du battement sont remises avec le reste : sans
   * elles, la frame suivante repartirait de l'état que la partie précédente a
   * laissé et déferait le calage d'un coup.
   */
  snapThrust(level: ThrustTier): void {
    const lv = THRUST_LEVELS[level];
    this.plumeClock = 0;
    this.plumeLen = lv.len;
    this.plumeRad = lv.rad;
    for (let i = 0; i < this.flames.length; i++) {
      const m = this.flames[i]!;
      const isCore = i % 2 === 1;
      m.scale.z = lv.len * (isCore ? 0.62 : 1);
      m.scale.x = lv.rad * (isCore ? 0.5 : 1);
      m.scale.y = m.scale.x;
    }
    this.flameOuter.opacity = lv.opacity * 0.55;
    this.flameCore.opacity = lv.opacity;
    this.flameOuter.color.setHex(lv.colour);
    this.flameCore.color.setHex(CORE_BY_TIER[level]);
  }

  /**
   * @param slide le drift, signé par le côté où le vaisseau glisse, 0 en dehors
   *   — `driftIntensity × driftSide`, l'échelle partagée. La traînée se courbe
   *   de l'autre côté et se disloque en proportion.
   */
  updateSmoke(frameDt: number, speed: number, level: ThrustTier, slide = 0): void {
    if (speed < 1) {
      for (const sp of this.smoke) sp.visible = false;
      return;
    }
    this.smokePhase = (this.smokePhase + (speed * frameDt) / TRAIL_LENGTH) % 1;
    this.wake += (slide - this.wake) * Math.min(1, frameDt * WAKE_EASE);
    const turbulence = Math.abs(this.wake);
    if (turbulence > 0.001) this.wakeClock += frameDt * WAKE_RATE;
    const half = SMOKE_COUNT / 2;
    const base = 0.2 + level * 0.11;

    for (let i = 0; i < SMOKE_COUNT; i++) {
      const sp = this.smoke[i]!;
      const side = i < half ? -1 : 1;
      // 0 à la tuyère, 1 à la queue. Borné par construction, d'où l'échelle de
      // sprite ci-dessous qui ne peut jamais devenir négative — un sprite
      // miroir plein écran est un bug que ce code a déjà payé.
      const p = (this.smokePhase + (i % half) / half) % 1;
      sp.visible = true;
      // Exactement nul quand le sillage est nul : `a + 0` vaut `a`, donc une
      // frame du mode attraction tombe sur les mêmes flottants qu'avant le sillage.
      const bend = -this.wake * WAKE_BEND * p;
      const jitter = turbulence * WAKE_JITTER * p * Math.sin(this.wakeClock + i * 2.4);
      sp.position.set(
        side * (1.15 + p * 1.05) + bend + jitter,
        1.0 + p * 0.85 + jitter * 0.5,
        -2.6 - p * TRAIL_LENGTH,
      );
      sp.scale.setScalar(0.45 + p * 1.35 + turbulence * p * 0.6);
      sp.material.opacity = base * Math.pow(Math.sin(p * Math.PI), 1.3);
    }
  }

  /** Les bouffées portent une distance parcourue, donc une remise à zéro doit les lâcher. */
  clearSmoke(): void {
    this.smokePhase = 0;
    this.wake = 0;
    this.wakeClock = 0;
    for (const sp of this.smoke) sp.visible = false;
  }

  /**
   * La coque cède : elle disparaît, une coque d'onde enfle tout autour et un
   * cœur bref marque l'instant. Déclenché une fois par `wreck` — un second
   * appel avant `resetExplosion` ne fait rien, la partie est déjà perdue.
   */
  explode(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.exploding = true;
    this.explodeAge = 0;
    this.body.visible = false;
    this.halo.visible = false;

    this.shockwave.visible = true;
    this.shockwave.scale.setScalar(0.001);
    this.shockwaveMaterial.uniforms.uOpacity!.value = 1;

    this.flash.visible = true;
    this.flash.scale.setScalar(0.001);
    this.flashMaterial.opacity = 1;
  }

  /**
   * Frame d'affichage du pouls : ne fait rien hors explosion, et s'éteint
   * de lui-même sans toucher à `destroyed` — la coque reste cachée une fois
   * l'onde dissipée, jusqu'à `resetExplosion`.
   */
  updateExplosion(frameDt: number): void {
    if (!this.exploding) return;
    this.explodeAge += frameDt;
    const t = Math.min(1, this.explodeAge / SHOCKWAVE_LIFE);

    // Démarre vite, ralentit en s'élargissant — un pouls, pas une chute. La
    // coque s'éteint plus vite qu'elle ne grandit : une sphère dont la surface
    // croît comme le carré du rayon garderait sinon trop de lumière à la fin.
    const eased = 1 - (1 - t) ** 3;
    this.shockwave.scale.setScalar(0.001 + eased * SHOCKWAVE_MAX_RADIUS);
    this.shockwaveMaterial.uniforms.uOpacity!.value = (1 - t) ** 3;

    const ft = Math.min(1, this.explodeAge / FLASH_LIFE);
    this.flash.scale.setScalar(0.001 + (1 - (1 - ft) ** 2) * FLASH_RADIUS);
    this.flashMaterial.opacity = (1 - ft) ** 3;
    // Éteint pour de bon, et pas seulement transparent : mesuré à l'écran, le
    // cœur laissait une bille grise dans l'onde bien après sa fin de vie.
    if (ft >= 1) this.flash.visible = false;

    if (t >= 1) {
      this.exploding = false;
      this.shockwave.visible = false;
      this.flash.visible = false;
      // L'onde s'est dissipée : l'épave prend la place de la coque. Couchée sur
      // le flanc et posée plus bas, pour qu'on la lise comme une carcasse et
      // non comme un vaisseau à l'arrêt.
      this.wreck.visible = true;
      this.wreck.rotation.set(0.18, 0.42, 1.05, 'YXZ');
      this.wreck.position.set(0.3, -0.25, -0.6);
    }
  }

  /** Reforme le vaisseau pour une nouvelle partie : l'inverse de `explode`. */
  /** Vrai quand l'épave est posée sur la piste : lu par la surface de mise au point. */
  get wrecked(): boolean {
    return this.wreck.visible;
  }

  resetExplosion(): void {
    this.destroyed = false;
    this.exploding = false;
    this.explodeAge = 0;
    this.body.visible = true;
    this.wreck.visible = false;
    this.shockwave.visible = false;
    this.flash.visible = false;
  }

  /**
   * Lueur de ramassage et d'impact. Pilotée par le client, pas par l'état de la
   * simulation : `halo` et `haloPow` ont quitté le noyau quand les événements
   * les ont remplacés.
   *
   * @param intensity 0 à 1, amortie par l'appelant.
   */
  setHalo(colour: number, intensity: number, power: number): void {
    if (intensity <= 0) {
      this.halo.visible = false;
      return;
    }
    this.haloMaterial.color.setHex(colour);
    this.halo.visible = true;
    this.haloMaterial.opacity = 0.85 * intensity * intensity * power;
    this.halo.scale.setScalar(0.55 + (1 - intensity) * 2.3 * power);
  }

  private buildHull(): void {
    this.body.add(hullBody());
  }

  /** Un cône large et diffus et un cœur étroit et brillant, par tuyère. */
  private buildPlumes(): { outer: MeshBasicMaterial; core: MeshBasicMaterial } {
    const geo = new ConeGeometry(1, 1, 12, 1, true);
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, 0, -0.5);

    const outer = new MeshBasicMaterial({
      color: 0x5fd8ff,
      transparent: true,
      opacity: 0.4,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    });
    const core = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.8,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    });

    for (const x of [-1.15, 1.15]) {
      const o = new Mesh(geo, outer);
      const c = new Mesh(geo, core);
      o.position.set(x, 1.0, -2.3);
      c.position.set(x, 1.0, -2.3);
      this.flames.push(o, c);
      this.body.add(o, c);
    }
    return { outer, core };
  }

  private buildSmoke(): void {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 96;
    const g = canvas.getContext('2d')!;
    const grd = g.createRadialGradient(48, 48, 0, 48, 48, 48);
    grd.addColorStop(0, 'rgba(255,255,255,0.95)');
    grd.addColorStop(0.35, 'rgba(255,255,255,0.40)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 96, 96);

    const map = new CanvasTexture(canvas);
    this.smokeMap = map;
    for (let i = 0; i < SMOKE_COUNT; i++) {
      const sp = new Sprite(
        new SpriteMaterial({ map, transparent: true, opacity: 0, depthWrite: false, fog: false }),
      );
      sp.visible = false;
      this.group.add(sp);
      this.smoke.push(sp);
    }
  }
}

/**
 * La coque : un M vu de face — deux ailes hautes, une épine centrale plus
 * basse, deux creux — avec ses nacelles, sa verrière et ses feux.
 *
 * Exportée pour le fantôme, qui est le même vaisseau vu à travers : avec un
 * `material`, chaque pièce le porte à la place du sien, et la silhouette reste
 * celle que le joueur connaît. Sans, ce sont les matériaux du joueur.
 */
export function hullBody(material?: Material): Group {
  const mat = (own: Material): Material => material ?? own;
  const P: Record<string, Point3> = {
    N: [0, 0.52, 2.9],
    T: [0, 1.05, -0.2],
    L: [-0.62, 0.46, -0.2],
    R: [0.62, 0.46, -0.2],
    B: [0, -0.02, -0.2],
    T2: [0, 0.86, -2.4],
    L2: [-0.48, 0.46, -2.4],
    R2: [0.48, 0.46, -2.4],
    B2: [0, 0.12, -2.4],
  };
  const hull = new Mesh(
    poly(
      [
        ['N', 'T', 'R'],
        ['N', 'R', 'B'],
        ['N', 'B', 'L'],
        ['N', 'L', 'T'],
        ['T', 'L', 'L2'],
        ['T', 'L2', 'T2'],
        ['R', 'T', 'T2'],
        ['R', 'T2', 'R2'],
        ['B', 'R', 'R2'],
        ['B', 'R2', 'B2'],
        ['L', 'B', 'B2'],
        ['L', 'B2', 'L2'],
        ['T2', 'L2', 'B2'],
        ['T2', 'B2', 'R2'],
      ],
      P,
    ),
    mat(new MeshLambertMaterial({ color: 0x36485f, side: DoubleSide })),
  );

  // aile en flèche, emplanture basse et bout relevé
  const W: Record<string, Point3> = {
    A: [0.52, 0.44, 1.0],
    B: [0.52, 0.44, -1.9],
    K1: [1.5, 1.3, -0.15],
    K2: [1.52, 1.3, -2.15],
    T1: [1.88, 1.02, -0.8],
    T2: [1.9, 1.02, -2.35],
  };
  const WL: Record<string, Point3> = {};
  for (const k of Object.keys(W)) WL[k] = [-W[k]![0], W[k]![1], W[k]![2]];
  const wingFaces = [
    ['A', 'K1', 'K2'],
    ['A', 'K2', 'B'],
    ['K1', 'T1', 'T2'],
    ['K1', 'T2', 'K2'],
  ] as const;
  const wingMat = mat(new MeshLambertMaterial({ color: 0x2b3b52, side: DoubleSide }));
  const wingR = new Mesh(poly(wingFaces, W), wingMat);
  const wingL = new Mesh(poly(wingFaces, WL), wingMat);

  // nacelles aux deux tiers de l'envergure
  const podGeo = new CylinderGeometry(0.26, 0.22, 1.15, 8);
  podGeo.rotateX(Math.PI / 2);
  const podMat = mat(new MeshLambertMaterial({ color: 0x1d2836 }));
  const pods = [-1.15, 1.15].map((x) => {
    const m = new Mesh(podGeo, podMat);
    m.position.set(x, 1.0, -1.7);
    return m;
  });

  const glowMat = mat(new MeshBasicMaterial({ color: 0x8af4ff, transparent: true, opacity: 0.95 }));
  const glowGeo = new CircleGeometry(0.23, 8);
  const glows = [-1.15, 1.15].map((x) => {
    const m = new Mesh(glowGeo, glowMat);
    m.position.set(x, 1.0, -2.29);
    m.rotation.y = Math.PI;
    return m;
  });

  const canopy = new Mesh(
    new BoxGeometry(0.44, 0.3, 1.05),
    mat(new MeshLambertMaterial({ color: 0x0d1620 })),
  );
  canopy.position.set(0, 0.96, 0.75);
  canopy.rotation.x = -0.12;

  const spine = new Mesh(
    new BoxGeometry(0.13, 0.06, 2.0),
    mat(new MeshBasicMaterial({ color: 0x25e2ff })),
  );
  spine.position.set(0, 1.07, -0.9);

  const strakeGeo = new BoxGeometry(0.09, 0.36, 0.72);
  const strakeMat = mat(new MeshBasicMaterial({ color: 0xff2f9a }));
  const strakes = [-1.51, 1.51].map((x) => {
    const m = new Mesh(strakeGeo, strakeMat);
    m.position.set(x, 1.46, -1.2);
    m.rotation.z = x < 0 ? 0.16 : -0.16;
    return m;
  });

  const g = new Group();
  g.add(hull, wingL, wingR, canopy, spine, ...pods, ...glows, ...strakes);
  return g;
}
